import { useState } from 'react';
import { products, productTypeLabels } from '@/mocks/products';
import type { Product } from '@/mocks/products';

export default function ProductsPage() {
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);

  let filtered = [...products];

  if (search) {
    const q = search.toLowerCase();
    filtered = filtered.filter(p =>
      p.name.toLowerCase().includes(q) ||
      p.description.toLowerCase().includes(q) ||
      p.type.toLowerCase().includes(q)
    );
  }

  if (typeFilter) {
    filtered = filtered.filter(p => p.type === typeFilter);
  }

  return (
    <div>
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
        <div>
          <h2 className="text-xl md:text-2xl font-bold text-stone-900">Ürünler</h2>
          <p className="text-sm text-stone-500 mt-0.5">Ürün kataloğunu yönetin</p>
        </div>
        <button className="flex items-center gap-2 px-4 py-2 bg-gold-500 text-white text-sm font-medium rounded-lg hover:bg-gold-600 transition-colors cursor-pointer whitespace-nowrap">
          <i className="ri-add-line" />Yeni Ürün Ekle
        </button>
      </div>

      {/* Search & Filter */}
      <div className="flex flex-col sm:flex-row gap-3 mb-5">
        <div className="relative flex-1">
          <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-stone-400 text-sm" />
          <input
            type="text"
            placeholder="Ürün adı veya açıklama ile ara..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-9 pr-4 py-2.5 bg-white border border-stone-200 rounded-lg text-sm text-stone-800 placeholder:text-stone-400 focus:outline-none focus:border-gold-400 focus:ring-1 focus:ring-gold-400/30"
          />
        </div>
        <select
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value)}
          className="px-3 py-2.5 bg-white border border-stone-200 rounded-lg text-sm text-stone-700 cursor-pointer focus:outline-none focus:border-gold-400"
        >
          <option value="">Tüm Ürün Tipleri</option>
          {Object.entries(productTypeLabels).map(([key, label]) => (
            <option key={key} value={key}>{label}</option>
          ))}
        </select>
      </div>

      {/* Product Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {filtered.map((product) => (
          <div
            key={product.id}
            onClick={() => setSelectedProduct(product)}
            className="bg-white rounded-xl border border-stone-200/80 overflow-hidden hover:border-stone-300 hover:shadow-sm transition-all cursor-pointer group"
          >
            {/* Image */}
            <div className="aspect-[4/3] bg-stone-100 relative overflow-hidden">
              <img
                src={product.imageUrl}
                alt={product.name}
                title={product.name + ' - Tablocum.11'}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
              />
              {!product.active && (
                <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                  <span className="text-white text-sm font-medium bg-black/60 px-3 py-1 rounded-full">Pasif</span>
                </div>
              )}
              <div className="absolute top-3 right-3">
                <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                  product.inStock ? 'bg-status-green/90 text-white' : 'bg-status-red/90 text-white'
                }`}>
                  {product.inStock ? 'Üretilebilir' : 'Stok Dışı'}
                </span>
              </div>
            </div>

            {/* Info */}
            <div className="p-4">
              <div className="flex items-start justify-between mb-2">
                <div>
                  <span className="text-[10px] text-stone-400 uppercase tracking-wider">{productTypeLabels[product.type]}</span>
                  <h4 className="text-sm font-semibold text-stone-800 mt-0.5 line-clamp-2">{product.name}</h4>
                </div>
              </div>
              <p className="text-xs text-stone-500 line-clamp-2 mb-3">{product.description}</p>
              <div className="flex items-center justify-between pt-3 border-t border-stone-100">
                <span className="text-lg font-bold text-stone-900">{product.price.toLocaleString('tr-TR')} ₺</span>
                <span className="text-xs text-stone-400 flex items-center gap-1">
                  <i className="ri-time-line" />
                  {product.productionTime}
                </span>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Product Detail Modal */}
      {selectedProduct && (
        <>
          <div className="fixed inset-0 bg-black/30 z-40" onClick={() => setSelectedProduct(null)} />
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
              <div className="aspect-[4/3] bg-stone-100 relative overflow-hidden rounded-t-2xl">
                <img
                  src={selectedProduct.detailImageUrl}
                  alt={selectedProduct.name}
                  title={selectedProduct.name + ' - Tablocum.11'}
                  className="w-full h-full object-cover"
                />
                <button
                  onClick={() => setSelectedProduct(null)}
                  className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center bg-white/90 rounded-full hover:bg-white transition-colors cursor-pointer"
                >
                  <i className="ri-close-line" />
                </button>
              </div>
              <div className="p-6">
                <span className="text-xs text-stone-400 uppercase tracking-wider">{productTypeLabels[selectedProduct.type]}</span>
                <h3 className="text-xl font-bold text-stone-900 mt-1">{selectedProduct.name}</h3>
                <p className="text-sm text-stone-500 mt-3 leading-relaxed">{selectedProduct.description}</p>

                <div className="grid grid-cols-2 gap-4 mt-6 pt-4 border-t border-stone-100">
                  <div>
                    <span className="text-xs text-stone-400 block">Fiyat</span>
                    <span className="text-lg font-bold text-stone-900">{selectedProduct.price.toLocaleString('tr-TR')} ₺</span>
                  </div>
                  <div>
                    <span className="text-xs text-stone-400 block">Üretim Süresi</span>
                    <span className="text-sm font-medium text-stone-800">{selectedProduct.productionTime}</span>
                  </div>
                  <div>
                    <span className="text-xs text-stone-400 block">Durum</span>
                    <span className={`inline-block text-xs font-medium px-2 py-0.5 rounded-full mt-0.5 ${
                      selectedProduct.inStock ? 'bg-status-green/10 text-status-green' : 'bg-status-red/10 text-status-red'
                    }`}>
                      {selectedProduct.inStock ? 'Üretilebilir' : 'Stok Dışı'}
                    </span>
                  </div>
                  <div>
                    <span className="text-xs text-stone-400 block">Aktiflik</span>
                    <span className={`inline-block text-xs font-medium px-2 py-0.5 rounded-full mt-0.5 ${
                      selectedProduct.active ? 'bg-status-green/10 text-status-green' : 'bg-status-gray/10 text-status-gray'
                    }`}>
                      {selectedProduct.active ? 'Aktif' : 'Pasif'}
                    </span>
                  </div>
                </div>

                <div className="flex gap-2 mt-6">
                  <button className="flex-1 px-4 py-2.5 bg-gold-500 text-white text-sm font-medium rounded-lg hover:bg-gold-600 transition-colors cursor-pointer">
                    Düzenle
                  </button>
                  <button
                    onClick={() => setSelectedProduct(null)}
                    className="px-4 py-2.5 text-sm text-stone-500 hover:text-stone-700 cursor-pointer"
                  >
                    Kapat
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}